import { Plus } from 'lucide-react';
import React, { FC, useMemo, useRef, useState } from 'react';

import { type Counterparty } from '@column/api';
import { Button, Popover, PopoverContent, PopoverTrigger } from '@column/ui';

import { ModalCounterparty } from '~/components/Dialog';
import { useCounterpartyOptions } from '~/components/fields/CounterpartyDropdown';
import { type HighlightSuffixCandidate, getHighlightedSuffix } from '~/lib/search/highlight';

import {
  type AccountCardRow,
  EmptyAccountTrigger,
  EntitySearchCommand,
  type EntitySearchOption,
  FadeSwap,
  SelectedAccountCard,
  highlightMatch,
  renderMatchFragment,
} from './AccountSelector';
import { FADE_ONLY_MOTION, OVERLAP_CELL, OVERLAP_GRID, POPOVER_CONTENT_CLASS, TRIGGER_HEIGHT_PX } from './constants';

// Fields the row doesn't otherwise show. When the search matched one of these
// (rather than the name, account, or routing number), the secondary line reveals
// it — labelled — so it's clear why the row matched.
const HIDDEN_MATCH_CANDIDATES: HighlightSuffixCandidate[] = [
  { field: 'BENEFICIARY_EMAIL', label: 'EMAIL' },
  { field: 'BENEFICIARY_PHONE', label: 'PHONE' },
  { field: 'LOCAL_ACCOUNT_NUMBER', label: 'LOCAL ACCT' },
  { field: 'LOCAL_BANK_CODE', label: 'LOCAL BANK' },
  { field: 'COUNTERPARTY_ID', label: 'ID' },
];

export interface CounterpartySelectorProps {
  value: string;
  onChange: (id: string) => void;
  onBlur: () => void;
  hasError?: boolean;
  /** Counterparty kind for the "New counterparty" modal (e.g. "ach", "wire"). */
  counterpartyType: string;
  /** `data-cy` hook applied to the field, matching the old FormField wrapper. */
  dataCy?: string;
}

/**
 * A single counterparty picker: a placeholder trigger that morphs into a
 * server-searched dropdown (with an adjacent "New" button opening the create
 * modal), collapsing into a detail card once chosen. Reusable across transfer
 * forms — wire `value`/`onChange`/`onBlur` to a form field and pass the
 * `counterpartyType` that the create modal should default to.
 */
export const CounterpartySelector: FC<CounterpartySelectorProps> = ({
  value,
  onChange,
  onBlur,
  hasError,
  counterpartyType,
  dataCy,
}) => {
  const [open, setOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Mirrors the search input so option rows can highlight the matched substring.
  const [query, setQuery] = useState('');
  // See the matching ref in BankAccountSelector: skip focus-restore when a selection
  // (rather than an escape/click-outside) collapses the field into its card.
  const selectedOnClose = useRef(false);
  const { counterparties, activeCounterparty, activeCounterpartyIsLoading, search, handleScrollEnd } =
    useCounterpartyOptions({ value });

  const options: EntitySearchOption[] = useMemo(
    () =>
      counterparties.map((cp) => {
        // If the query matched a field the row doesn't show (ID, email, etc.),
        // reveal it in place of the account/routing line so the match is visible.
        const matchedHidden = getHighlightedSuffix(cp.search_metadata, HIDDEN_MATCH_CANDIDATES);
        return {
          id: cp.id,
          primary: highlightMatch(cp.name || cp.description || cp.wire?.beneficiary_name || 'Unnamed', query),
          // Normally the full account number then routing number (the separating
          // slash and "ACCT"/"RTN" prefixes in a lighter gray than the monospace
          // numbers); when a hidden field matched, that labelled field instead.
          secondary: matchedHidden ? (
            <span className="font-mono">
              <span className="text-muted-foreground/60">{matchedHidden.label} </span>
              {renderMatchFragment(matchedHidden.fragment)}
            </span>
          ) : cp.account_number || cp.routing_number ? (
            <span className="font-mono">
              {cp.account_number && (
                <>
                  <span className="text-muted-foreground/60">ACCT </span>
                  {highlightMatch(cp.account_number, query)}
                </>
              )}
              {cp.account_number && cp.routing_number && <span className="text-muted-foreground/60"> / </span>}
              {cp.routing_number && (
                <>
                  <span className="text-muted-foreground/60">RTN </span>
                  {highlightMatch(cp.routing_number, query)}
                </>
              )}
            </span>
          ) : undefined,
        };
      }),
    [counterparties, query]
  );

  // Fall back to the dropdown-list entry so a failed or forbidden detail fetch
  // never blanks the card (mirrors BankAccountSelector). placeholderData only
  // applies while the query is pending; once it errors, activeCounterparty is
  // undefined.
  const counterparty = (activeCounterparty ?? counterparties.find((cp) => cp.id === value)) as Counterparty | undefined;
  const rows: AccountCardRow[] = counterparty
    ? [
        { label: 'Counterparty ID', value: counterparty.id || '—', monospaced: true },
        { label: 'Account number', value: counterparty.account_number || '—', monospaced: true },
        { label: 'Routing number', value: counterparty.routing_number || '—', monospaced: true },
      ]
    : [];

  const handleCreated = (created: Counterparty) => {
    setIsModalOpen(false);
    setOpen(false);
    onChange(created.id);
  };

  // The card fills the full width on its own — no "New" button beside it once
  // selected. Card and dropdown overlap in one cell (see OVERLAP_GRID above).
  return (
    <>
      <div className={OVERLAP_GRID}>
        <SelectedAccountCard
          className={OVERLAP_CELL}
          open={Boolean(value)}
          title={counterparty?.name || counterparty?.description || 'Counterparty'}
          rows={rows}
          isLoading={!counterparty && activeCounterpartyIsLoading}
          hasError={hasError}
          onClear={() => onChange('')}
        />
        <FadeSwap visible={!value} className={OVERLAP_CELL}>
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1" data-cy={dataCy}>
              <Popover
                open={open}
                onOpenChange={(next) => {
                  setOpen(next);
                  // Reset the server search (and the highlight query) so reopening
                  // starts unfiltered rather than with a stale query the (remounted,
                  // empty) input no longer shows.
                  if (!next) {
                    onBlur();
                    setQuery('');
                    search('');
                  }
                }}
              >
                <PopoverTrigger asChild>
                  <EmptyAccountTrigger label="Select a counterparty" hasError={hasError} />
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  sideOffset={-TRIGGER_HEIGHT_PX}
                  className={POPOVER_CONTENT_CLASS}
                  {...FADE_ONLY_MOTION}
                  onCloseAutoFocus={(event) => {
                    // The field collapses to the (aria-hidden) card on select, so
                    // don't let Radix restore focus into that hidden subtree.
                    if (selectedOnClose.current) {
                      selectedOnClose.current = false;
                      event.preventDefault();
                    }
                  }}
                >
                  <EntitySearchCommand
                    options={options}
                    searchPlaceholder="Search counterparties…"
                    emptyMessage="No counterparties found"
                    onSearch={(q) => {
                      setQuery(q);
                      search(q);
                    }}
                    onSelect={(id) => {
                      selectedOnClose.current = true;
                      onChange(id);
                      setOpen(false);
                      // Selecting closes via setOpen (not onOpenChange), so reset here too.
                      setQuery('');
                      search('');
                    }}
                    onScrollEnd={handleScrollEnd}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <Button variant="secondary" type="button" onClick={() => setIsModalOpen(true)}>
              <Plus />
              New
            </Button>
          </div>
        </FadeSwap>
      </div>
      <ModalCounterparty
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        callback={handleCreated}
        type={counterpartyType}
      />
    </>
  );
};
